import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations, { detectLanguage, languageNames } from '../i18n/translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // First check localStorage, then detect from browser
    const stored = localStorage.getItem('afy_language');
    return stored || detectLanguage();
  });

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);

  const setLanguage = useCallback(async (newLang) => {
    if (!['en', 'de', 'fr', 'it', 'es'].includes(newLang)) return;
    
    setLanguageState(newLang);
    localStorage.setItem('afy_language', newLang);
    
    // Update user preference if logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/language`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ language: newLang }),
        });
      } catch (e) {
        console.error('Failed to update language preference');
      }
    }
  }, []);

  // Sync with user preference on login
  const syncWithUserPreference = useCallback((userLang) => {
    if (userLang && ['en', 'de', 'fr', 'it', 'es'].includes(userLang)) {
      setLanguageState(userLang);
      localStorage.setItem('afy_language', userLang);
    }
  }, []);

  const value = {
    language,
    setLanguage,
    t,
    syncWithUserPreference,
    languages: languageNames,
    availableLanguages: ['de', 'en', 'fr', 'it', 'es'],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
